import React, { useState } from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import { tokens, sharedStyles } from "../styles";

// Per-DB card.
//   • Top-right shows TPs Assigned + Crates Assigned (capacity bar / X/Y
//     dropped — those were speculative until Phase-0 backend lands).
//   • Routes assigned to this DB render as inline chips on the body.
//   • Bundle (📦 crate) chips dropped from the collapsed view to keep the
//     card compact at scale — full breakdown lives in the expanded
//     "View Details" panel below.
//   • Action stack:
//       Select Locality (outline)  →  + Add Bundle (green)  →  View Details
//   • Expanded details list each customer with an inline "×" so the
//     executive can unassign one cx without opening Unassign-All. Mirrors
//     the per-cx unassign payload web ships (`cp-routing.vue:538-585`).

const DBCard = ({
  db,
  bundles = [],
  customers = [],
  onPressAddBundle,
  onPressSelectRoutes,
  onPressUnassignAll,
  onPressUnassignCustomer,
}) => {
  const [open, setOpen] = useState(false);
  const assigned = Number(db?.assigned_tp_count) || 0;
  const cratesAssigned = Array.isArray(bundles) ? bundles.length : 0;
  const routes = Array.isArray(db?.routes)
    ? db.routes
    : db?.route_id
      ? [{ route_id: db.route_id, route_name: db.route_name || db.route_id }]
      : [];

  return (
    <View style={[sharedStyles.card, styles.card]}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Text variant="title_16" style={styles.name} numberOfLines={1}>
            {db?.db_name || "—"}
          </Text>
        </View>
        <View style={styles.headerRight}>
          <Text variant="caption_2" style={styles.statLine}>
            <Text variant="caption_2" style={styles.statLabel}>
              TPs Assigned:{" "}
            </Text>
            <Text variant="caption_2" style={styles.statValue}>
              {assigned}
            </Text>
          </Text>
          <Text variant="caption_2" style={styles.statLineMuted}>
            <Text variant="caption_2" style={styles.statLabelMuted}>
              Crates Assigned:{" "}
            </Text>
            <Text variant="caption_2" style={styles.statValueMuted}>
              {cratesAssigned}
            </Text>
          </Text>
        </View>
      </View>

      {routes.length > 0 ? (
        <View style={styles.routeChipsRow}>
          {routes.map((r) => (
            // key off route_name (= cx_locality string, unique per pill
            // after the locality-derived backend fix). route_id can
            // collide when one cp_db_routes_v2 row covers multiple
            // cx_localities.
            <View key={String(r.route_name)} style={styles.routeChip}>
              <Text variant="caption_2" style={styles.routeChipText}>
                ✦ {r.route_name || r.route_id}
              </Text>
            </View>
          ))}
        </View>
      ) : (
        <Text variant="caption_2" style={styles.noRoutes}>
          No localities selected. Tap Select Locality to start.
        </Text>
      )}

      <View style={styles.actionStack}>
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => onPressSelectRoutes?.(db)}
          style={[styles.actionBtn, styles.actionOutline]}
        >
          <Text variant="caption_2" style={styles.actionOutlineText}>
            Select Locality ({routes.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.85}
          disabled={routes.length === 0}
          onPress={() => onPressAddBundle?.(db)}
          style={[
            styles.actionBtn,
            styles.actionPrimary,
            routes.length === 0 && styles.actionDisabled,
          ]}
        >
          <Text variant="caption_2" style={styles.actionPrimaryText}>
            + Add Crates
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => setOpen((v) => !v)}
          style={styles.detailsToggle}
        >
          <View style={styles.detailsToggleInner}>
            <View
              style={[
                styles.detailsChev,
                open ? styles.detailsChevUp : styles.detailsChevDown,
              ]}
            >
              <ChevronRight
                width={10}
                height={10}
                pathFill={tokens.textSecondary}
              />
            </View>
            <Text variant="caption_2" style={styles.detailsToggleText}>
              {open ? "Hide Details" : "View Details"}
            </Text>
          </View>
        </TouchableOpacity>
      </View>

      {open ? (
        <View style={styles.expanded}>
          <Text variant="caption_2" style={styles.expandedHeading}>
            Assignment Details
          </Text>
          <Text variant="caption_2" style={styles.expandedBody}>
            Total orders: {assigned}
            {bundles.length
              ? ` · ${bundles.length} crate${bundles.length === 1 ? "" : "s"}`
              : ""}
          </Text>
          {bundles.length ? (
            <View style={styles.crateList}>
              {bundles.map((b) => (
                <View key={`exp-${b.crate_id || b.id}`} style={styles.crateRow}>
                  <View style={styles.crateAccent} />
                  <View style={styles.crateRowBody}>
                    <Text
                      variant="caption_2"
                      style={styles.crateRowTitle}
                      numberOfLines={1}
                    >
                      {b.crate_id || b.label}
                      {b.crate_size ? ` · ${b.crate_size}` : ""}
                    </Text>
                    {b.customers && b.customers.length > 0 ? (
                      <Text
                        variant="caption_2"
                        style={styles.crateRowMeta}
                        numberOfLines={2}
                      >
                        {b.customers.slice(0, 4).join(", ")}
                        {b.customers.length > 4 ? "…" : ""}
                      </Text>
                    ) : null}
                  </View>
                </View>
              ))}
            </View>
          ) : null}
          {customers.length > 0 ? (
            <View style={styles.cxList}>
              <Text variant="caption_2" style={styles.cxHeading}>
                Customers
              </Text>
              {(() => {
                const cxByRoute = new Map();
                customers.forEach((cx) => {
                  const rid = String(cx?.route_id || "__none__");
                  const grp = cxByRoute.get(rid) || {
                    route_id: cx?.route_id,
                    route_name: cx?.route_name,
                    items: [],
                  };
                  grp.items.push(cx);
                  cxByRoute.set(rid, grp);
                });
                return Array.from(cxByRoute.values()).map((g) => (
                  <View
                    key={String(g.route_id || "__none__")}
                    style={styles.cxRouteGroup}
                  >
                    <Text variant="caption_2" style={styles.cxRouteHeader}>
                      ✦ {g.route_name || g.route_id || "Route"}
                    </Text>
                    {g.items.map((cx) => (
                      <View
                        key={String(cx?.cx_id || cx?.user_id)}
                        style={styles.cxRow}
                      >
                        <View style={styles.cxBody}>
                          <Text
                            variant="caption_2"
                            style={styles.cxName}
                            numberOfLines={1}
                          >
                            {cx?.user_name || "—"}
                          </Text>
                          <Text
                            variant="caption_2"
                            style={styles.cxMeta}
                            numberOfLines={1}
                          >
                            {cx?.locality || cx?.address || ""}
                          </Text>
                        </View>
                        {onPressUnassignCustomer ? (
                          <TouchableOpacity
                            activeOpacity={0.7}
                            onPress={() => onPressUnassignCustomer(cx, db)}
                            hitSlop={{
                              top: 8,
                              bottom: 8,
                              left: 8,
                              right: 8,
                            }}
                            style={styles.cxRemove}
                          >
                            <Text
                              variant="caption_2"
                              style={styles.cxRemoveText}
                            >
                              ×
                            </Text>
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    ))}
                  </View>
                ));
              })()}
            </View>
          ) : null}
          {onPressUnassignAll && assigned > 0 ? (
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => onPressUnassignAll(db)}
              style={[styles.actionBtn, styles.unassignAll]}
            >
              <Text variant="caption_2" style={styles.unassignAllText}>
                Unassign All
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

export default DBCard;

const styles = StyleSheet.create({
  card: { paddingVertical: tokens.spacing.large },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: { flex: 1, marginRight: tokens.spacing.small },
  headerRight: { alignItems: "flex-end" },
  name: { color: tokens.textPrimary, fontWeight: "700", fontSize: 15 },

  statLine: { fontSize: 13 },
  statLabel: { color: tokens.textSecondary, fontSize: 12 },
  statValue: { color: tokens.textPrimary, fontSize: 13, fontWeight: "700" },
  statLineMuted: { fontSize: 12, marginTop: 2 },
  statLabelMuted: { color: tokens.textSecondary, fontSize: 12 },
  statValueMuted: {
    color: tokens.textPrimary,
    fontSize: 12,
    fontWeight: "700",
  },

  routeChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: tokens.spacing.medium,
  },
  routeChip: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 4,
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.pill.preferred.bg,
    borderWidth: 1,
    borderColor: tokens.pill.preferred.border,
    marginRight: 6,
    marginBottom: 6,
  },
  routeChipText: {
    color: tokens.pill.preferred.fg,
    fontSize: 11,
    fontWeight: "700",
  },
  noRoutes: {
    color: tokens.textTertiary,
    fontSize: 12,
    marginTop: tokens.spacing.medium,
    fontStyle: "italic",
  },

  actionStack: {
    marginTop: tokens.spacing.medium,
  },
  actionBtn: {
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: 10,
    borderRadius: tokens.radius.md,
    alignItems: "center",
    marginBottom: tokens.spacing.small,
  },
  actionOutline: {
    backgroundColor: tokens.surface,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
  },
  actionOutlineText: {
    color: tokens.textPrimary,
    fontSize: 13,
    fontWeight: "700",
  },
  actionPrimary: { backgroundColor: tokens.primary.bg },
  actionPrimaryText: {
    color: tokens.primary.fg,
    fontSize: 13,
    fontWeight: "700",
  },
  actionDisabled: { opacity: 0.45 },
  detailsToggle: {
    paddingVertical: 6,
    alignItems: "center",
  },
  detailsToggleInner: {
    flexDirection: "row",
    alignItems: "center",
  },
  detailsChev: {
    width: 12,
    height: 12,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 6,
  },
  detailsChevDown: { transform: [{ rotate: "90deg" }] },
  detailsChevUp: { transform: [{ rotate: "-90deg" }] },
  detailsToggleText: {
    color: tokens.textSecondary,
    fontSize: 12,
    fontWeight: "600",
  },

  expanded: {
    marginTop: tokens.spacing.small,
    paddingTop: tokens.spacing.medium,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: tokens.borderLight,
  },
  expandedHeading: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: tokens.spacing.small,
  },
  expandedBody: { color: tokens.textPrimary, fontSize: 13 },

  crateList: { marginTop: tokens.spacing.medium },
  crateRow: {
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: tokens.spacing.small,
  },
  crateAccent: {
    width: 3,
    backgroundColor: tokens.primary.bg,
    borderRadius: 2,
    marginRight: tokens.spacing.small,
  },
  crateRowBody: { flex: 1 },
  crateRowTitle: {
    color: tokens.textPrimary,
    fontSize: 13,
    fontWeight: "600",
  },
  crateRowMeta: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginTop: 2,
  },

  cxList: { marginTop: tokens.spacing.medium },
  cxHeading: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: tokens.spacing.tiny,
  },
  cxRouteGroup: { marginTop: tokens.spacing.small },
  cxRouteHeader: {
    color: tokens.pill.preferred.fg,
    fontSize: 11,
    fontWeight: "700",
    marginBottom: 4,
  },
  cxRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  cxBody: { flex: 1, marginRight: tokens.spacing.small },
  cxName: { color: tokens.textPrimary, fontSize: 13, fontWeight: "600" },
  cxMeta: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
  cxRemove: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: tokens.error,
    alignItems: "center",
    justifyContent: "center",
  },
  cxRemoveText: {
    color: tokens.error,
    fontSize: 16,
    fontWeight: "700",
    lineHeight: 18,
  },

  unassignAll: {
    marginTop: tokens.spacing.medium,
    backgroundColor: tokens.surface,
    borderWidth: 1,
    borderColor: tokens.error,
    alignSelf: "flex-start",
  },
  unassignAllText: { color: tokens.error, fontSize: 13, fontWeight: "700" },
});
